import { useSearchParams } from '@remix-run/react'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { CheckCircle, WarningHexagon } from 'iconoir-react'
import { camelCase, startCase } from 'lodash'
import moment from 'moment'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import FormCheckBox from '../form/FormCheckBox'
import FormCombo from '../form/FormCombo'
import FormTextArea from '../form/FormTextArea'
import { AppointmentDetails } from './AppointmentDetails'
import { SchedulingWizardContext } from './SchedulingWizard'
import FormInput from 'app/components/form/FormInput'
import FormPhone from 'app/components/form/FormPhone'
import { SmartForm } from 'app/components/form/SmartForm'
import { LocationJson } from 'app/models/Location'
import { CustomQuestionType, SchedulingConfigurationJson } from 'app/models/SchedulingConfiguration'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'
import { INSURANCE_LIST } from 'types/InsuranceList'
import { OptionType } from 'types/OptionType'

export const patientSchema = (customQuestions?: CustomQuestionType[]) => {
  const extraFields =
    customQuestions?.reduce((acc, q) => {
      if (!q.enabled) return acc

      // Handle opt-in without fieldName
      const fieldName = q.fieldName || `optIn_${q.id}`
      const displayName = q.fieldName ? startCase(q.fieldName) : `Opt-In`

      if (q.fieldType === `checkbox`) {
        return {
          ...acc,
          [camelCase(fieldName)]: zfd
            .checkbox()
            .transform((val) => (val ? `Checked` : `Unchecked`))
            .refine((val) => (q.required ? val === `Checked` : true), {
              message: `${displayName} must be checked`,
            }),
        }
      }
      return {
        ...acc,
        [camelCase(fieldName)]: z
          .string()
          .refine((val) => (q.required ? !!val : true), { message: `${displayName} is required` }),
      }
    }, {}) ?? {}

  return withZod(
    z
      .object({
        firstName: z.string().refine((val) => val !== ``, { message: `First name is required` }),
        lastName: z.string().refine((val) => val !== ``, { message: `Last name is required` }),
        email: z.string().email(),
        phone: z.string().refine(
          (val) => {
            const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/
            return phoneRegex.test(val) || /^\d{10}$/.test(val)
          },
          {
            message: `Phone number is not valid`,
          },
        ),
        dob: z
          .string()
          .refine(
            (val) => {
              return val !== `` && val.length === 10 && moment(val, `MM/DD/YYYY`).isValid()
            },
            {
              message: `Date of birth is not valid`,
            },
          )
          .transform((val) => {
            // Kolla appt required date format 'YYYY-MM-DD'
            return moment(val, `MM/DD/YYYY`).format(`YYYY-MM-DD`)
          }),
        insurance: z.string().min(1, { message: `Please select an insurance provider` }),
        // otherInsurance is required only if insurance is other
        otherInsurance: z.string().optional(),
        patientType: z.string(),
        operatoryId: z.string(),
        day: z.string(),
        time: z.string(),
        providerId: z.string(),
        utm_source: z.string().optional(),
        utm_medium: z.string().optional(),
        utm_campaign: z.string().optional(),
        utm_content: z.string().optional(),
        gclid: z.string().optional(),
        fbclid: z.string().optional(),
        ...extraFields,
      })
      .superRefine((data, ctx) => {
        if (data.insurance === `Other` && !data.otherInsurance) {
          ctx.addIssue({
            code: `custom`,
            message: `Please provide the name of your insurance provider`,
            path: [`otherInsurance`],
          })
        }
      }),
  )
}

export function AppointmentForm({
  location,
  schedulingConfiguration,
  startDate,
}: {
  location?: LocationJson
  schedulingConfiguration?: SchedulingConfigurationJson
  startDate: string | undefined
}): ReactElement {
  const {
    apptType,
    back,
    selectedDaySlotIndex,
    providers,
    patient,
    selectedTimeSlot,
    setIsFormComplete,
    setPatient,
    setSelectedInsurance,
    patientType,
    selectedAvailability,
    setIsSaving,
    setCurrentStep,
    setCreatedAppointment,
    setAppointmentUnavailable,
  } = useContext(SchedulingWizardContext)
  const [searchParams] = useSearchParams()
  const [selectedInsuranceOption, setSelectedInsuranceOption] = useState<OptionType>()

  useEffect(() => {
    setSelectedInsurance(selectedInsuranceOption?.value)
    // Trigger form validation when insurance selection changes
    validateForm()
  }, [selectedInsuranceOption, schedulingConfiguration?.customQuestions])

  const validateForm = () => {
    const form = document.getElementById(`appointment-form`) as HTMLFormElement
    if (form) {
      const formData = new FormData(form)
      const data = Object.fromEntries(formData.entries())
      const textareas = form.querySelectorAll(`textarea`)
      const textAreaInputs = Array.from(textareas).reduce((acc, textarea) => {
        acc[textarea.name] = textarea.value
        return acc
      }, {})

      bg(async () => {
        const result = await patientSchema(schedulingConfiguration?.customQuestions).validate({
          ...data,
          ...textAreaInputs,
          insurance: selectedInsuranceOption?.value, // Include the selected insurance value
        })
        if (result.error) {
          debug(`scheduling`, `appointment form error`, result.error)
          setIsFormComplete(false)
        } else {
          setPatient({
            firstName: data[`firstName`] as string,
            lastName: data[`lastName`] as string,
            email: data[`email`] as string,
            phone: data[`phone`] as string,
            dob: data[`dob`] as string,
          })
          setIsFormComplete(true)
        }
      })
    }
  }

  const onCreateAppointmentComplete = (data) => {
    if (data.appointment) {
      setCreatedAppointment(data.appointment)
    } else if (data.unavailable) {
      setAppointmentUnavailable(true)
    } else if (data.error) {
      logError(data.error)
    }
    setIsSaving(false)
    setCurrentStep(5)
  }
  useEffect(() => {
    const form = document.getElementById(`appointment-form`) as HTMLFormElement
    const textareas = form.querySelectorAll(`textarea`)
    form.addEventListener(`input`, validateForm)
    textareas.forEach((textarea) => {
      textarea.addEventListener(`input`, validateForm)
    })
    return () => {
      form.removeEventListener(`input`, validateForm)

      textareas.forEach((textarea) => {
        textarea.removeEventListener(`input`, validateForm)
      })
    }
  }, [document.getElementById(`appointment-form`), schedulingConfiguration?.customQuestions])

  return (
    <div
      className={`flex grow flex-col gap-y-6 overflow-y-auto md:overflow-hidden ${
        back ? `animate-fade-right` : `animate-fade-left`
      } animate-duration-[400ms]`}
    >
      <AppointmentDetails
        apptType={apptType!}
        selectedDay={moment(startDate).add(selectedDaySlotIndex, `days`).toISOString()}
        provider={providers.find((p) => p.id === selectedAvailability?.providerId)!}
        selectedTime={selectedTimeSlot!.time}
      />
      <div className="flex grow flex-col gap-2 md:overflow-y-auto">
        <SmartForm
          id="appointment-form"
          className="!mt-0"
          validator={patientSchema(schedulingConfiguration?.customQuestions)}
          defaultValues={{ ...patient }}
          hiddenInputs={[
            { name: `id`, value: location?.id },
            { name: `patientType`, value: patientType },
            { name: `operatoryId`, value: selectedAvailability?.operatoryId },
            { name: `day`, value: moment(startDate).add(selectedDaySlotIndex, `days`).format(`YYYY-MM-DD`) },
            { name: `time`, value: selectedTimeSlot?.time },
            { name: `providerId`, value: providers.find((p) => p.id === selectedAvailability?.providerId)!.vendorId },
            { name: `insurance`, value: selectedInsuranceOption?.value },
            ...Array.from(searchParams)
              .filter(
                ([key, value]) =>
                  value && [`utm_source`, `utm_medium`, `utm_campaign`, `utm_content`, `gclid`, `fbclid`].includes(key),
              )
              .map(([key, value]) => ({ name: key, value })),
          ]}
          shouldPreventDefault
          reloadDocument={false}
          onSubmit={() => setIsSaving(true)}
          onPreventDefault={onCreateAppointmentComplete}
          hideButtons
          method="POST"
          action={`/api/scheduling/locations/${location?.id}/appointment-types/${apptType?.id}/appointments`}
        >
          <FormInput
            name="firstName"
            labelClassName="text-neutral-700 text-sm required"
            label="First Name"
            cols={3}
            required
          />
          <FormInput
            name="lastName"
            labelClassName="text-neutral-700 text-sm required"
            label="Last Name"
            cols={3}
            required
          />
          <FormInput
            cols={3}
            name="dob"
            labelClassName="text-neutral-700 text-sm required"
            label="Birth Date"
            placeholder="mm/dd/yyyy"
            className="placeholder:text-neutral-400"
            type="text"
            onChange={(evt) => {
              let value = evt.currentTarget.value

              // Remove any non-numeric characters except slashes
              value = value.replace(/[^0-9/]/g, ``)

              // Remove slashes to get just the numbers
              const numbersOnly = value.replace(/\//g, ``)

              // Rebuild the value with proper formatting
              let formattedValue = ``

              if (numbersOnly.length >= 2) {
                formattedValue = numbersOnly.substring(0, 2)
                if (numbersOnly.length > 2) {
                  formattedValue += `/` + numbersOnly.substring(2, 4)
                  if (numbersOnly.length > 4) {
                    formattedValue += `/` + numbersOnly.substring(4, 8)
                  }
                }
              } else {
                formattedValue = numbersOnly
              }

              evt.currentTarget.value = formattedValue

              // Validate if we have a complete date
              if (formattedValue.length === 10) {
                const date = moment(formattedValue, `MM/DD/YYYY`)
                if (!date.isValid()) {
                  evt.currentTarget.value = ``
                }
              }
            }}
            required
          />
          <FormPhone
            name="phone"
            labelClassName="text-neutral-700 text-sm required"
            label="Phone Number"
            cols={3}
            maxLength={10}
            minLength={10}
            required
          />
          <FormInput name="email" labelClassName="text-neutral-700 text-sm required" label="Email" cols={6} required />
          <FormCombo
            name="_insurance"
            label="Insurance"
            labelClassName="text-neutral-700 text-sm required"
            optionsContainerClassName="!max-h-[220px]"
            options={[
              { name: `I'll pay for myself`, value: `Self Paid`, id: `Self Paid` },
              { name: `Other`, value: `Other`, id: `Other` },
              ...INSURANCE_LIST.map((insurance) => ({
                name: insurance,
                value: insurance,
                id: insurance,
              })),
            ]}
            showSearchInDropdown
            value={selectedInsuranceOption}
            onChange={(option) => setSelectedInsuranceOption(option)}
            placeholder="Select your insurance provider"
            required
            cols={6}
          />
          {schedulingConfiguration?.notifyInOutNetwork &&
            selectedInsuranceOption &&
            selectedInsuranceOption?.value !== `Self Paid` &&
            selectedInsuranceOption?.value !== `Other` && (
              <div
                className={classNames(
                  `col-span-12 -mt-3 flex items-center gap-2.5 text-body-xs md:col-span-6`,
                  schedulingConfiguration?.insurancesAccepted?.includes(selectedInsuranceOption?.value) &&
                    `text-success-default`,
                  !schedulingConfiguration?.insurancesAccepted?.includes(selectedInsuranceOption?.value) &&
                    !schedulingConfiguration?.insurancesRejected?.includes(selectedInsuranceOption?.value) &&
                    `text-amber-800`,
                  schedulingConfiguration?.insurancesRejected?.includes(selectedInsuranceOption?.value) &&
                    `text-red-600`,
                )}
              >
                {schedulingConfiguration?.insurancesAccepted?.includes(selectedInsuranceOption?.value) && (
                  <>
                    <CheckCircle width={20} height={20} />
                    <p>Your selected insurance is in network.</p>
                  </>
                )}
                {!schedulingConfiguration?.insurancesAccepted?.includes(selectedInsuranceOption?.value) &&
                  !schedulingConfiguration?.insurancesRejected?.includes(selectedInsuranceOption?.value) && (
                    <>
                      <WarningHexagon width={20} height={20} />
                      <p>Your selected insurance is considered out of network.</p>
                    </>
                  )}
                {schedulingConfiguration?.insurancesRejected?.includes(selectedInsuranceOption?.value) && (
                  <>
                    <WarningHexagon width={20} height={20} />
                    <p>Your selected insurance is not accepted.</p>
                  </>
                )}
              </div>
            )}
          {selectedInsuranceOption?.value === `Other` && (
            <FormInput
              name="otherInsurance"
              label="Insurance Provider"
              labelClassName="text-neutral-700 text-sm required"
              placeholder="Insurance Provider Name (Other)"
              cols={6}
            />
          )}
          {schedulingConfiguration?.customQuestions?.map((question) => {
            if (!question.enabled) return null
            if (question.fieldType === `one-line`) {
              return (
                <FormInput
                  key={question.id}
                  name={camelCase(question.fieldName)}
                  label={startCase(question.fieldName)}
                  labelClassName={`text-neutral-700 text-sm ${question.required ? `required` : ``}`}
                  required={question.required}
                  cols={6}
                  className="placeholder:text-neutral-400"
                />
              )
            } else if (question.fieldType === `multiple-lines`) {
              return (
                <FormTextArea
                  key={question.id}
                  name={camelCase(question.fieldName)}
                  label={startCase(question.fieldName)}
                  labelClassName={`text-neutral-700 text-sm ${question.required ? `required` : ``}`}
                  className="h-24 resize-none overflow-y-auto px-4 py-3.5 placeholder:text-neutral-400"
                  placeholder="Anything we should know?"
                  required={question.required}
                  cols={6}
                  maxLength={200}
                />
              )
            } else if (question.fieldType === `checkbox`) {
              const fieldName = question.fieldName || `optIn_${question.id}`

              if (!question.fieldName && question.optIn) {
                return (
                  <div key={question.id} className="col-span-12 flex items-start md:col-span-6">
                    <FormCheckBox
                      name={camelCase(fieldName)}
                      label=""
                      checkboxClassName="mt-0.5"
                      required={question.required}
                    />
                    <label
                      htmlFor={camelCase(fieldName)}
                      className="ml-1.5 w-11/12 cursor-pointer break-words text-label-sm leading-[140%] text-neutral-400 md:w-full"
                    >
                      <pre className="whitespace-pre-wrap font-[inherit]">
                        {question.description || `I agree to receive marketing communications`}
                      </pre>
                    </label>
                  </div>
                )
              }

              return (
                <div key={question.id} className="col-span-12 md:col-span-6">
                  <FormCheckBox
                    name={camelCase(question.fieldName)}
                    label={question.fieldName.replace(/\w+/g, startCase)}
                    labelClassName={`text-neutral-700 text-ui-sm ${question.required ? `required` : ``}`}
                    required={question.required}
                  />
                  {question.optIn && question.description && (
                    <pre className="ml-6 mt-2 w-11/12 whitespace-pre-wrap break-words font-[inherit] text-sm text-neutral-400">
                      {question.description}
                    </pre>
                  )}
                </div>
              )
            }
            return null
          })}
        </SmartForm>
      </div>
    </div>
  )
}
